import {Component, inject, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {AccessService} from "../../../services/access.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../../services/data.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {ViewMode} from "../../../interfaces";

@Component({
    selector: 'app-product-group-detail',
    imports: [ReactiveFormsModule],
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-globe me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Produktgruppe anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Produktgruppe bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Produktgruppe
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <div class="row">
                        <div class="col">
                            <label>Nummer</label>
                            @if (viewMode == ViewMode.VIEW) {
                                <p>{{ form.value.position }}</p>
                            }
                            @else {
                                <input type="number" class="form-control" formControlName="position" min="1" max="9">
                            }
                        </div>
                        <div class="col">
                            <label>Name</label>
                            @if (viewMode == ViewMode.VIEW) {
                                <p>{{ form.value.name }}</p>
                            } @else {
                                <input type="text" class="form-control" formControlName="name">
                            }
                        </div>
                    </div>
                </form>

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary" (click)="cancel()">Zurück</button>
                        <button type="button" class="btn btn-danger float-end" (click)="delete()" [disabled]="productGroup?.products != '0'">Löschen</button>
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>
            </div>
        </div>
    `
})
export class ProductGroupDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);

    ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_productGroup!: string;
    viewMode!: ViewMode;
    productGroup: any;

    ngOnInit(): void {
        this.id_productGroup = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_productGroup == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            position: [null, Validators.required],
            name: [null, Validators.required],
        });

        if (this.id_productGroup !== '0') {
            this.getProductGroup();
        }
    }

    getProductGroup(): void {
        this.dataService.request('ProductGroup/get', {
            id: this.id_productGroup
        }).subscribe(response => {
            this.productGroup = response.productGroup;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.productGroup.id,
            position: this.productGroup.position,
            name: this.productGroup.name,
        });
    }

    save(): void {
        this.dataService.request('ProductGroup/save', {
            productGroup: this.form.getRawValue(),
        }).subscribe(() => {
            this.router.navigate(['/productgroup']);
            this.toastrService.success('Die Produktgruppe wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('ProductGroup/update', {
            productGroup: this.form.getRawValue(),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getProductGroup();
            this.toastrService.success('Die Produktgruppe wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/productgroup']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
        this.getProductGroup();
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Die Produktgruppe wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('ProductGroup/delete', {
                    id: this.id_productGroup
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Die Produktgruppe wurde gelöscht');
                });
            }
        });
    }

}
